import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { z } from 'zod'
import { Alert, Badge, Box, Button, Group, Stack, Tabs, Title } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { useSystemDetails } from '@/features/systems/hooks/useSystemDetails'
import { SystemHeader } from '@/features/systems/components/SystemHeader'
import { systemQueryOptions } from '@systems/api/systemQueries.tsx'
import { systemWorkItemsQueryOptions } from '@/features/incidentIntegration/api/incidentIntegrationQueries'
import { useEffect, useState } from 'react'
import { SystemOverviewTab } from "@systems/components/SystemOverviewTab.tsx"
import { UploadedArtifactsTable } from "@systems/components/artifactManagement/UploadedArtifactsTable.tsx"
import { useNewArtifactTracking } from '@systems/hooks/useNewArtifactTracking'
import { UserRole } from "@/features/auth/domain/auth.types.ts";
import { ProtectedComponent } from "@/features/auth/components/ProtectedComponent.tsx";

/**
 * Search parameters schema for the system details route
 * @param systemName - Optional system name for context/breadcrumbs
 * @param customerId - Optional customer ID for navigation context
 */
const SearchSchema = z.object({
  systemName: z.string().optional(),
  customerId: z.string().optional(),
})

export const Route = createFileRoute('/_authed/systems/$systemId/')({
  validateSearch: SearchSchema,
  loader: ({context: {queryClient}, params: {systemId}}) => {
    queryClient.prefetchQuery(systemQueryOptions(systemId))
    queryClient.prefetchQuery(systemWorkItemsQueryOptions(systemId))
    return {}
  },
  errorComponent: SystemDetailsErrorComponent,
  component: SystemDetailsPage,
})

/**
 * Main system details page displaying comprehensive system information in tabbed interface.
 * Serves as the primary landing page for individual system management and monitoring.
 */
function SystemDetailsPage() {
  const {systemId} = Route.useParams()
  const {isSignalRConnected} = useSystemDetails(systemId)
  const {badgeArtifactIds, pillArtifactIds, clearBadge, clearPills} = useNewArtifactTracking(systemId)
  const [activeTab, setActiveTab] = useState<string | null>('overview')

  // Clear badge when switching to files tab, clear pills when leaving
  const handleTabChange = (value: string | null) => {
    // Clear pills when leaving files tab
    if (activeTab === 'files' && value !== 'files') {
      clearPills()
    }

    setActiveTab(value)

    // Clear badge when entering files tab
    if (value === 'files') {
      clearBadge()
    }
  }

  return (
    <Stack gap={0}>
      <SystemHeader
        systemId={systemId}
        isConnected={isSignalRConnected}
        isLoading={false}
      />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        style={{flex: 1}}
        pl='2.375rem'
        orientation="horizontal"
      >
        <Box
          pt="md"
          bg='var(--mantine-color-body)'
          pos='sticky'
          top='calc(var(--app-shell-header-offset, 0rem) + var(--page-header-height, 0px))'
          style={{zIndex: 98}}
          mx={-10} // the margin/padding counteracting here is to account for the tab content box shadows as we scroll (they would previously appear)
          px={10}>
          <Tabs.List>
            <Tabs.Tab value="overview" aria-label="system-overview-tab">
              <Title fw={400}>
                Overview
              </Title>
            </Tabs.Tab>

            <ProtectedComponent requiredRole={UserRole.Support}>
              <Tabs.Tab value="files" aria-label="system-files-tab">
                <Group gap="xs">
                  <Title fw={400}>
                    Files
                  </Title>
                  {badgeArtifactIds.size > 0 && (
                    <Badge color="red" size="sm">+{badgeArtifactIds.size}</Badge>
                  )}
                </Group>
              </Tabs.Tab>
            </ProtectedComponent>
          </Tabs.List>
        </Box>

        <Tabs.Panel value="overview" pt="lg" h="100%">
          <SystemOverviewTab
            systemId={systemId}
            isConnected={isSignalRConnected}
          />
        </Tabs.Panel>

        <ProtectedComponent requiredRole={UserRole.Support}>
          <Tabs.Panel value="files" pt="md" h="100%">
            <UploadedArtifactsTable systemId={systemId} newArtifactIds={pillArtifactIds}/>
          </Tabs.Panel>
        </ProtectedComponent>
      </Tabs>
    </Stack>
  )
}

/**
 * Error component props for system details page
 * @param error - The error object containing details about what went wrong
 */
/**
 * Error boundary component for system details page failures.
 * Provides user-friendly error display with retry and navigation options.
 */
function SystemDetailsErrorComponent({error}: { error: Error }) {
  const router = useRouter()
  const queryErrorResetBoundary = useQueryErrorResetBoundary()

  useEffect(() => {
    queryErrorResetBoundary.reset()
  }, [queryErrorResetBoundary])

  return (
    <Alert
      icon={<IconInfoCircle size={16}/>}
      title="Error Loading System"
      color="red"
      variant="light"
    >
      {error.message || 'Unable to load system details. Please try again.'}
      <Group mt="md">
        <Button variant="light" onClick={() => router.invalidate()}>
          Retry
        </Button>
        <Button variant="outline" onClick={() => router.navigate({to: '/systems'})}>
          Return to System List
        </Button>
      </Group>
    </Alert>
  )
}